import React, { useMemo, useState } from 'react';
import { useAccount } from 'wagmi';
import BackendWalletSignInPanel from '../../components/auth/BackendWalletSignInPanel';
import ConnectWalletButton from '../../components/connectWallet/ConnectWalletButton';
import SwitchNetworkButton from '../../components/connectWallet/SwitchNetworkButton';
import { targetChainId, DIRECT_PAYMENT, TRACKED_PAYMENT } from '../../constant/constant';
import { useBackendWalletAuth } from '../../hooks/useBackendWalletAuth';
import { shortenWalletAddress } from '../../utils/commonFunction';
import DeliveryModePicker from './components/DeliveryModePicker';
import IncomingRequestsPanel from './components/IncomingRequestsPanel';
import SendPaymentBasics from './components/SendPaymentBasics';
import DirectPayment from './DirectPayment';
import TrackedPayment from './TrackedPayment';

const Pay = () => {
    const [receiver, setReceiver] = useState('');
    const [amount, setAmount] = useState('');
    const [token, setToken] = useState({});
    const [paymentMode, setPaymentMode] = useState(DIRECT_PAYMENT);
    const [isRecipientContract, setIsRecipientContract] = useState(false);

    const { isConnected, chainId, address: walletAddress } = useAccount();
    const {
        status: authStatus,
        error: authError,
        signIn,
        isReady: isBackendReady,
        user: backendUser,
    } = useBackendWalletAuth({
        walletAddress,
        enabled: isConnected && chainId === targetChainId,
    });

    const basics = useMemo(
        () => ({
            userName: receiver.trim(),
            amount,
            token,
        }),
        [receiver, amount, token],
    );

    const isDirect = paymentMode === DIRECT_PAYMENT;

    return (
        <div className="crypto-page relative min-h-[calc(100vh-80px)] overflow-hidden">
            <div className="relative z-10 mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
                {!isConnected ? (
                    <div className="flex min-h-[60vh] flex-col items-center justify-center animate-fade-in">
                        <ConnectWalletButton />
                    </div>
                ) : chainId !== targetChainId ? (
                    <div className="flex min-h-[60vh] flex-col items-center justify-center animate-fade-in">
                        <SwitchNetworkButton />
                    </div>
                ) : !isBackendReady ? (
                    <BackendWalletSignInPanel
                        walletAddress={walletAddress}
                        status={authStatus}
                        error={authError}
                        onRetry={signIn}
                        title="Register to send payments"
                        description="Sign a free message to create your CronPay account before sending direct or tracked payments. Payment history and receipts are saved to your account."
                    />
                ) : (
                    <>
                        <div className="mb-4 rounded-xl border border-teal-200 bg-teal-50/70 px-4 py-3 text-sm text-teal-900 dark:border-emerald-500/20 dark:bg-emerald-500/[0.07] dark:text-emerald-100">
                            <i className="fa-solid fa-circle-check mr-2 text-teal-600 dark:text-emerald-400" />
                            Signed in as{' '}
                            <span className="font-mono font-semibold">
                                {backendUser?.name || shortenWalletAddress(walletAddress)}
                            </span>
                            . Payments will be saved to your account.
                        </div>
                        <div className="mb-8 animate-fade-in-up">
                            <h1 className="text-3xl font-semibold tracking-tight text-zinc-900 md:text-4xl dark:text-white">
                                Send crypto
                            </h1>
                            <p className="mt-2 max-w-2xl text-sm text-zinc-600 md:text-base dark:text-zinc-400">
                                Enter payment details, choose a delivery method, and
                                confirm before sending.
                            </p>
                        </div>

                        <div className="grid grid-cols-1 gap-6 animate-fade-in-up delay-100 xl:grid-cols-[minmax(0,1fr)_360px]">
                            <div className="space-y-5">
                                <div className="rounded-2xl border border-zinc-200 bg-white p-6 shadow-sm transition-colors md:rounded-3xl md:p-8 dark:border-zinc-800 dark:bg-zinc-950/50 dark:shadow-none">
                                    <div className="mb-5 flex items-start gap-3 border-b border-zinc-200 pb-4 dark:border-zinc-800">
                                        <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-teal-100 text-xs font-bold text-teal-800 ring-1 ring-teal-200 dark:bg-emerald-500/15 dark:text-emerald-300 dark:ring-emerald-500/20">
                                            1
                                        </span>
                                        <div>
                                            <h3 className="text-base font-semibold text-zinc-900 dark:text-white">
                                                Payment details
                                            </h3>
                                            <p className="mt-0.5 text-xs text-zinc-600 dark:text-zinc-400">
                                                Receiver, token, and amount to send.
                                            </p>
                                        </div>
                                    </div>

                                    <SendPaymentBasics
                                        receiver={receiver}
                                        onReceiverChange={setReceiver}
                                        amount={amount}
                                        onAmountChange={setAmount}
                                        token={token}
                                        onTokenChange={setToken}
                                        walletAddress={walletAddress}
                                        onRecipientContractChange={setIsRecipientContract}
                                    />

                                    {isRecipientContract && receiver && (
                                        <p className="mt-3 flex items-center gap-1 text-[11px] text-rose-600 dark:text-rose-300">
                                            <i className="fa-solid fa-circle-exclamation text-[10px]" />
                                            Payments cannot be sent to a contract address.
                                        </p>
                                    )}

                                    <div className="mt-6 border-t border-zinc-200 pt-6 dark:border-zinc-800">
                                        <div className="mb-4 flex items-start gap-3">
                                            <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-teal-100 text-xs font-bold text-teal-800 ring-1 ring-teal-200 dark:bg-emerald-500/15 dark:text-emerald-300 dark:ring-emerald-500/20">
                                                2
                                            </span>
                                            <div>
                                                <h3 className="text-base font-semibold text-zinc-900 dark:text-white">
                                                    Delivery method
                                                </h3>
                                                <p className="mt-0.5 text-xs text-zinc-600 dark:text-zinc-400">
                                                    How should these funds be delivered?
                                                </p>
                                            </div>
                                        </div>
                                        <DeliveryModePicker
                                            value={paymentMode}
                                            onChange={setPaymentMode}
                                        />
                                    </div>

                                    <div className="mt-6 border-t border-zinc-200 pt-6 dark:border-zinc-800">
                                        <div className="mb-5 flex items-start gap-3">
                                            <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-teal-100 text-xs font-bold text-teal-800 ring-1 ring-teal-200 dark:bg-emerald-500/15 dark:text-emerald-300 dark:ring-emerald-500/20">
                                                3
                                            </span>
                                            <div>
                                                <h3 className="text-base font-semibold text-zinc-900 dark:text-white">
                                                    {isDirect
                                                        ? 'Confirm direct payment'
                                                        : 'Milestone schedule'}
                                                </h3>
                                                <p className="mt-0.5 text-xs text-zinc-600 dark:text-zinc-400">
                                                    {isDirect
                                                        ? 'Add an optional note and send instantly.'
                                                        : 'Set escrow milestones, then create the tracked payment.'}
                                                </p>
                                            </div>
                                        </div>

                                        {isDirect ? (
                                            <DirectPayment
                                                embedded
                                                basics={basics}
                                            />
                                        ) : (
                                            <TrackedPayment
                                                embedded
                                                basics={basics}
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>

                            <aside className="xl:sticky xl:top-6 self-start animate-fade-in-up delay-200">
                                <IncomingRequestsPanel />
                            </aside>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default Pay;
